import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Input, Button, Row, Col } from "reactstrap";
import PropTypes from "prop-types";

const MonthlyParkingOrderUpdateModal = ({ isOpen, toggle, order, onSave }) => {
    const [editType, setEditType] = useState("");
    const [saving, setSaving] = useState(false);
    const today = new Date();
    const todayYmd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

    const [form, setForm] = useState({
        status: "",
        payment_type: "cod",
        total: "",
        quantity: "",
        customer_first_name: "",
        customer_last_name: "",
        customer_email: "",
        customer_phone: "",
    });

    const toYMD = (v) => {
        if (!v) return "";
        const s = String(v).trim();
        const dpart = s.includes(" ") ? s.split(" ")[0] : s.includes("T") ? s.split("T")[0] : s;

        const iso = dpart.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
        if (iso) return `${iso[1]}-${iso[2].padStart(2, "0")}-${iso[3].padStart(2, "0")}`;

        const us = dpart.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (us) return `${us[3]}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`;

        return dpart; // last resort, already yyyy-mm-dd
    };

    const originalYmd = toYMD(order?.next_payment_date) || todayYmd;

    const addYearsYmd = (ymd, years) => {
        const [y, m, d] = ymd.split("-").map(Number);
        const dt = new Date(y, m - 1, d);
        dt.setFullYear(dt.getFullYear() + years);
        const yy = dt.getFullYear();
        const mm = String(dt.getMonth() + 1).padStart(2, "0");
        const dd = String(dt.getDate()).padStart(2, "0");
        return `${yy}-${mm}-${dd}`;
    };

    useEffect(() => {
        if (order) {
            const baseYmdRaw = order.next_payment_date || "";
            const baseYmd = toYMD(baseYmdRaw) || toYMD(new Date().toISOString());
            const nextYmd = (order.editType === "pause") ? addYearsYmd(baseYmd, 10) : baseYmd;

            setEditType(order.editType || "status_payment");
            setForm({
                status: order.status || "",
                payment_type: order.payment_type || "cod",
                total: order.total || "",
                quantity: order.quantity || "",
                customer_first_name: order.customer_first_name || "",
                customer_last_name: order.customer_last_name || "",
                customer_email: order.customer_email || "",
                customer_phone: order.customer_phone || "",
                next_payment_date: nextYmd,
                pending_renewal_order_date: todayYmd,
                order_date: toYMD(order?.date_created) || todayYmd,
                check_number: order.check_number || "",
            });
        }
    }, [order]);

    const handleChange = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

    const handleSave = async () => {
        if (!order) return;
        setSaving(true);

        const payload = { id: order.id };
        Object.keys(form).forEach((k) => {
            if (form[k] !== "" && form[k] !== null) payload[k] = form[k];
        });

        try {
            await onSave(payload);
            toggle(); // close modal after successful save
        } finally {
            setSaving(false);
        }
    };

    const renderContent = () => {
        switch (editType) {
            case "pause": {

                return (
                    <div>
                        <label>Pause Subscription Until:</label>
                        <Input
                            type="date"
                            value={form.next_payment_date || ""}
                            onChange={(e) => handleChange("next_payment_date", e.target.value)}
                        />
                    </div>
                );
            }

            case "pending-renewal-order": {
                const today = new Date();
                const todayYmd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

                return (
                    <div>
                        <label>Create Pending Renewal Order For:</label>
                        <Input
                            type="date"
                            value={form.pending_renewal_order_date || todayYmd}
                            onChange={(e) => handleChange("pending_renewal_order_date", e.target.value)}
                        />
                    </div>
                );
            }

            case "order-date": {
                const defaultDate = toYMD(order?.date_created) || todayYmd;
                return (
                    <div>
                        <label>Change Order Created Date:</label>
                        <Input
                            type="date"
                            value={form.order_date || defaultDate}
                            onChange={(e) => handleChange("order_date", e.target.value)}
                        />
                    </div>
                );
            }

            case "subscription-status":
                return (
                    <div className="mb-3">
                        <label>Subscription Status</label>
                        <Input
                            type="select"
                            value={form.status}
                            onChange={(e) => {
                                handleChange("status", e.target.value);
                                console.log({
                                    subscription_id: order?.subscription_id || order?.id,
                                    new_status: e.target.value,
                                });
                            }}
                        >
                            {[
                                { value: "active", label: "Active" },
                                { value: "pending", label: "Pending" },
                                { value: "on-hold", label: "On Hold" },
                                { value: "cancelled", label: "Cancelled" },
                                { value: "expired", label: "Expired" },
                                { value: "switched", label: "Switched" },
                            ].map((s) => (
                                <option key={s.value} value={s.value}>
                                    {s.label}
                                </option>
                            ))}
                        </Input>
                    </div>
                );

            case "subscription-payment-method":
                return (
                    <div className="mb-3">
                        <label>Payment Method</label>
                        <Input
                            type="select"
                            value={form.payment_type}
                            onChange={(e) => {
                                handleChange("payment_type", e.target.value);
                                console.log({
                                    subscription_id: order?.subscription_id || order?.id,
                                    new_payment_method: e.target.value,
                                });
                            }}
                        >
                            {[
                                { value: "stripe_cc", label: "Stripe Credit Card" },
                                { value: "stripe_applepay", label: "Stripe Apple Pay" },
                                { value: "stripe_ach", label: "Stripe ACH" },
                                { value: "qbo", label: "QBO" },
                                { value: "cod", label: "Cash" },
                                { value: "cheque", label: "Check" },
                            ].map((m) => (
                                <option key={m.value} value={m.value}>
                                    {m.label}
                                </option>
                            ))}
                        </Input>
                    </div>
                );

            default:
                return (
                    <>
                        <div className="mb-3">
                            <label>Status</label>
                            <Input
                                type="select"
                                value={form.status}
                                onChange={(e) => handleChange("status", e.target.value)}
                            >
                                {[
                                    { value: "pending_payment", label: "Pending Payment" },
                                    { value: "processing", label: "Processing" },
                                    { value: "on-hold", label: "On Hold" },
                                    { value: "completed", label: "Completed" },
                                    { value: "cancelled", label: "Cancelled" },
                                    { value: "refunded", label: "Refunded" },
                                    { value: "failed", label: "Failed" },
                                ].map((s) => (
                                    <option key={s.value} value={s.value}>
                                        {s.label}
                                    </option>
                                ))}
                            </Input>
                        </div>
                        <div className="mb-3">
                            <label>Payment Method</label>
                            <Input
                                type="select"
                                value={form.payment_type}
                                onChange={(e) => handleChange("payment_type", e.target.value)}
                            >
                                {[
                                    { value: "stripe_cc", label: "Stripe Credit Card" },
                                    { value: "stripe_applepay", label: "Stripe Apple Pay" },
                                    { value: "stripe_ach", label: "Stripe ACH" },
                                    { value: "cod", label: "Cash" },
                                    { value: "cheque", label: "Check" },
                                ].map((m) => (
                                    <option key={m.value} value={m.value}>
                                        {m.label}
                                    </option>
                                ))}
                            </Input>
                        </div>
                        {form.payment_type === "cheque" && (
                            <div className="mb-3">
                                <label>Check No:</label>
                                <Input
                                    type="text"
                                    value={form.check_number || ""}
                                    onChange={(e) => handleChange("check_number", e.target.value)}
                                    placeholder="Enter check number"
                                />
                            </div>
                        )}
                    </>
                );
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle}>
            <ModalHeader toggle={toggle}>
                {editType === "total"
                    ? "Edit Total"
                    : editType === "quantity"
                        ? "Edit Quantity"
                        : editType === "customer"
                            ? "Edit Customer"
                            : "Update Order"}
            </ModalHeader>
            <ModalBody>{renderContent()}</ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={handleSave} disabled={saving}>
                    {saving ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                    {saving ? "Saving..." : "Save"}
                </Button>
                <Button color="secondary" onClick={toggle}>
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>
    );
};

MonthlyParkingOrderUpdateModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    order: PropTypes.object,
    onSave: PropTypes.func.isRequired,
};

export default MonthlyParkingOrderUpdateModal;